import { useState } from 'react';
import {
  Banner,
  Box,
  Button,
  Form,
  Link,
  Paragraph,
  PanelHeader,
  PanelSection,
  PanelTitle,
  Stack,
  TextField,
} from '@customerio/pluma-components/react';
import { isEmptyString } from '@/utils/string';
import { useLoginFlow } from '../login-flow';

const EMPTY_CODE_ERROR = "Authentication code can't be blank.";

export function TwoFactorStep() {
  const { twoFactorAuthCode, isSubmitting, setTwoFactorAuthCode, submitTwoFactor } = useLoginFlow();
  const [error, setError] = useState('');

  // Mirrors the Ember 2FA controller's client-side validation: a blank code
  // shows an inline error and blocks submit.
  const validate = (value: string): boolean => {
    const message = isEmptyString(value) ? EMPTY_CODE_ERROR : '';
    setError(message);
    return message === '';
  };

  const onSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!validate(twoFactorAuthCode)) {
      return;
    }
    return submitTwoFactor();
  };

  return (
    <>
      <PanelHeader>
        <PanelTitle level="1">Enter your two-factor app authentication code</PanelTitle>
      </PanelHeader>
      <PanelSection>
        <Form id="login-2fa-form" onSubmit={onSubmit}>
          <Stack gap="200">
            <TextField
              className="fs-mask"
              name="2fa_code"
              label="Authentication code"
              autoComplete="one-time-code"
              value={twoFactorAuthCode}
              error={error}
              onInput={(event) => {
                setTwoFactorAuthCode(event.target.value);
                validate(event.target.value);
              }}
              autoFocus
              shouldAllowAutofill
            />

            <Box>
              <Button
                type="submit"
                variant="primary"
                icon="chevron-right"
                iconPosition="trailing"
                w="full"
                isDisabled={!isEmptyString(error)}
                isLoading={isSubmitting}
              >
                Log in to Customer.io
              </Button>
            </Box>

            <Banner>
              To access your code, open your authentication app (ie. Google Authenticator, 1Password, etc) and paste the
              code provided.
            </Banner>

            <Paragraph>
              If you don't have access to your authenticator app, you can enter a{' '}
              <Link
                href="https://docs.customer.io/two-factor-auth/#i-lost-my-deviceim-locked-out-what-do-i-do"
                variant="secondary"
                isExternal
              >
                one-time recovery code
              </Link>
              .
            </Paragraph>
          </Stack>
        </Form>
      </PanelSection>
    </>
  );
}
